"use client";

import { Link } from "@/i18n/navigation";
import { ArrowRightIcon } from "@heroicons/react/24/outline";

export default function Button({
  children,
  href,
  onClick,
  icon: Icon,
  variant = "primary", // primary | accent | sand | transparent
  className = "",
  external = false,
  ...props
}) {
  const baseClasses =
    "group inline-flex h-[56px] items-center justify-center gap-3 rounded-2xl px-7 text-[16px] font-bold transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none disabled:hover:translate-y-0 whitespace-nowrap";

  // Rôles alignés sur la charte couleurs (src/docs/…charte-couleurs…v1.1.md) :
  // primary = bleu de marque, accent = vert de conversion (seule variante à
  // utiliser pour appeler/devis/envoyer), sand = secondaire blanc/bordure bleue.
  const variants = {
    primary: "bg-brand-900 text-white shadow-premiumSm hover:bg-brand-950",

    accent: "bg-cta-700 text-white shadow-cta hover:bg-cta-800 active:bg-cta-900",

    sand:
      "border-2 border-brand-700 bg-white text-brand-900 shadow-premiumSm hover:border-cta-300 hover:bg-cta-50 hover:text-cta-900",

    transparent:
      "border border-white/25 bg-white/10 text-white shadow-[0_14px_35px_rgba(0,0,0,0.18)] backdrop-blur-md hover:bg-white/16 hover:border-white/35",
  };

  const iconColor =
    variant === "primary"
      ? "text-white/75"
      : variant === "accent"
      ? "text-white"
      : variant === "transparent"
      ? "text-white"
      : "text-brand-700 group-hover:text-cta-700";

  const content = (
    <>
      {Icon && (
        <Icon
          className={`h-5 w-5 shrink-0 transition-transform duration-300 group-hover:scale-110 ${iconColor}`}
        />
      )}

      <span>{children}</span>

      {!Icon && (
        <ArrowRightIcon className="h-5 w-5 shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
      )}
    </>
  );

  const finalClassName = `${baseClasses} ${variants[variant] || variants.primary} ${className}`;

  if (href) {
    if (external || href.startsWith("tel:") || href.startsWith("mailto:")) {
      return (
        <a href={href} onClick={onClick} className={finalClassName} {...props}>
          {content}
        </a>
      );
    }

    return (
      <Link href={href} onClick={onClick} className={finalClassName} {...props}>
        {content}
      </Link>
    );
  }

  return (
    <button onClick={onClick} className={finalClassName} {...props}>
      {content}
    </button>
  );
}
